import { useLayoutEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { LogoMark } from '@/components/ui/LogoMark';
import { Icon, type IconName } from '@/components/ui/Icon';
import { useThemeStore } from '@/shared/stores/theme';
import { useMaxWidth, BP } from '@/shared/hooks/useMediaQuery';
import { useAuthStore } from '@/shared/stores/auth';

export interface NavItem {
  id: string;
  to: string;
  he: string;
  en?: string;
  icon: IconName;
  badge?: string | number;
}

export interface NavSection {
  he: string;
  en?: string;
  items: NavItem[];
  /** When true the section is a collapsible group, closed by default. */
  collapsible?: boolean;
}

interface SidebarProps {
  brand: string;
  sections: NavSection[];
}

const OPEN_KEY = 'merkaziya-sidebar-open-groups';
function loadOpenGroups(): Record<string, boolean> {
  try { return JSON.parse(localStorage.getItem(OPEN_KEY) || '{}'); } catch { return {}; }
}

export function Sidebar({ brand, sections }: SidebarProps) {
  const userCollapsed = useThemeStore((s) => s.sidebarCollapsed);
  const toggle = useThemeStore((s) => s.toggleSidebar);
  const narrow = useMaxWidth(BP.rail);
  const location = useLocation();
  const tenant = useAuthStore((s) => s.tenant);
  const navRef = useRef<HTMLElement>(null);
  const [indTop, setIndTop] = useState<number | null>(null);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(loadOpenGroups);

  // Force the icon rail whenever the viewport is narrow (also on first mount at
  // a narrow width — the previous transition-only effect missed that case and
  // left the full sidebar crushing the page). Above the breakpoint, honor the
  // user's manual collapse preference.
  const collapsed = narrow || userCollapsed;

  function toggleGroup(he: string) {
    setOpenGroups((prev) => {
      const next = { ...prev, [he]: !prev[he] };
      try { localStorage.setItem(OPEN_KEY, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  }

  // A collapsible group is shown expanded when the user opened it OR it contains
  // the active route (so the current page is never hidden).
  function groupExpanded(section: NavSection): boolean {
    if (!section.collapsible) return true;
    if (openGroups[section.he]) return true;
    return section.items.some((i) => location.pathname === i.to
      || (i.to !== '/admin' && location.pathname.startsWith(i.to + '/')));
  }

  // מחוון הפריט הפעיל מחליק למיקום של ה-NavLink הפעיל (עיצוב v2).
  useLayoutEffect(() => {
    const el = navRef.current?.querySelector<HTMLElement>('.nav-item.active');
    setIndTop(el ? el.offsetTop : null);
  }, [location.pathname, collapsed, sections, openGroups]);

  return (
    <aside
      style={{
        position: 'relative',
        zIndex: 2,
        flex: 'none',
        width: collapsed ? 'var(--sidebar-w-c)' : 'var(--sidebar-w)',
        height: '100vh',
        padding: 14,
        paddingInlineEnd: 0,
        display: 'flex',
        transition: 'width 200ms ease',
      }}
    >
      <div
        className="glass"
        style={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
          padding: collapsed ? '16px 8px' : '16px 12px',
          borderRadius: 16,
        }}
      >
        {/* לוגו GoCall + "מבית קליינטו" (עיצוב v2). מכווץ = הסימן בלבד.
            כפתור הכיווץ צף בפינה כדי לא להזיז את הלוגו מהמרכז. */}
        <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '6px 8px 4px' }}>
          <LogoMark size={collapsed ? 30 : 30} variant={collapsed ? 'mark' : 'full'} />
          {!collapsed && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 1, fontSize: 10.5, letterSpacing: 0.2, color: 'var(--muted)', fontWeight: 500 }}>
              <img src="/kliento-symbol.jpg" alt="" style={{ width: 11, height: 11, borderRadius: 2 }} />
              מבית קליינטו
            </span>
          )}
          {!collapsed && brand && <strong style={{ marginTop: 4, fontSize: 'var(--t-xs)', color: 'var(--text-2)' }}>{brand}</strong>}
          {/* The manual toggle only makes sense above the breakpoint; when narrow
              the rail is forced, so hide it. */}
          {!narrow && (
            <button
              type="button"
              onClick={toggle}
              className="btn ghost"
              aria-label={collapsed ? 'הרחב תפריט' : 'כווץ תפריט'}
              aria-expanded={!collapsed}
              style={collapsed
                ? { marginTop: 8, padding: 0, height: 26, width: 26, color: 'var(--muted)' }
                : { position: 'absolute', top: 0, insetInlineStart: -4, padding: 0, height: 26, width: 26, color: 'var(--faint)' }}
            >
              <Icon name={collapsed ? 'chevronD' : 'chevron'} size={13} />
            </button>
          )}
        </div>

        {tenant && !collapsed && (
          <div
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
              padding: '9px 12px', borderRadius: 'var(--r-2)',
              background: 'var(--glass-soft)', border: '1px solid var(--glass-line-2)',
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3, minWidth: 0 }}>
              <span style={{ fontSize: 11, color: 'var(--muted)' }}>מרכזייה</span>
              <span style={{ fontWeight: 600, fontSize: 13.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tenant.name}</span>
            </div>
            <span aria-hidden="true" style={{ flex: 'none', width: 8, height: 8, borderRadius: 99, background: 'var(--ok)' }} />
          </div>
        )}

        <nav ref={navRef} style={{ position: 'relative', flex: 1, overflowY: 'auto', overflowX: 'hidden', display: 'flex', flexDirection: 'column', gap: 2, margin: '0 -2px', padding: '0 2px' }}>
          {indTop != null && <div className="nav-ind" aria-hidden="true" style={{ top: indTop, insetInline: 2 }} />}
          {sections.map((section) => {
            const expanded = groupExpanded(section);
            return (
            <div key={section.he || 'main'} style={{ display: 'flex', flexDirection: 'column', gap: 2, marginBottom: section.he ? 10 : 0 }}>
              {!collapsed && section.he !== '' && (
                section.collapsible ? (
                  <button
                    type="button"
                    onClick={() => toggleGroup(section.he)}
                    aria-expanded={expanded}
                    style={{
                      display: 'flex', alignItems: 'center', width: '100%',
                      background: 'none', border: 'none', cursor: 'pointer',
                      fontSize: 'var(--t-xs)', color: 'var(--muted)',
                      fontWeight: 600, padding: '8px 12px 4px', fontFamily: 'inherit',
                    }}
                  >
                    <Icon name={expanded ? 'chevronD' : 'chevron'} size={12} />
                    <span style={{ marginInlineStart: 6 }}>{section.he}</span>
                  </button>
                ) : (
                  <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', fontWeight: 600, padding: '8px 12px 4px' }}>
                    {section.he}
                  </div>
                )
              )}
              {(collapsed || expanded) && section.items.map((item) => (
                <NavLink
                  key={item.id}
                  to={item.to}
                  end={item.to.split('/').length <= 2}
                  className={({ isActive }) => 'nav-item' + (isActive ? ' active' : '')}
                  style={collapsed ? { justifyContent: 'center', padding: 0 } : undefined}
                  title={collapsed ? item.he : undefined}
                >
                  <Icon name={item.icon} size={18} />
                  {!collapsed && <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.he}</span>}
                  {!collapsed && item.badge != null && (
                    <span
                      style={{
                        marginInlineStart: 'auto',
                        background: 'var(--accent)',
                        color: 'var(--on-accent)',
                        padding: '0 7px',
                        borderRadius: 'var(--r-pill)',
                        fontSize: 'var(--t-xs)',
                        fontWeight: 600,
                      }}
                    >
                      {item.badge}
                    </span>
                  )}
                </NavLink>
              ))}
            </div>
            );
          })}
        </nav>
      </div>
    </aside>
  );
}
